import CachedIcon from '@mui/icons-material/Cached'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Card, CardHeader, Chip, Dialog, IconButton, Tooltip } from '@mui/material'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import { HttpEtapasABM } from '../api/httpEtapasABM'
import { EtapaRequestFields, EtapaViewFields } from '../api/schemas'
import { Etapa } from '../types'
import { DialogEtapaABM } from './DialogEtapaABM'

import { ItecGridColsDef as GridColsDef, ItecColFilters, ItecDataGrid, useItecDataGrid } from 'src/lib/itaptec/datagrid/src'
import { GridActionsCellItem } from '@mui/x-data-grid'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { EntityWithIdAndDescriptionFields, PaisLogoFields, schemaSelectionOption } from 'src/types'
import { z } from 'zod'
import { CountryAvatar } from "../../../../../../components/icons/CountryAvatar"
import { WrapperTable } from '../../components'

const schemaFiltroEtapas = z.object({
  [EtapaRequestFields.CodSistemaPrincipal]: schemaSelectionOption.optional(),
  [EtapaRequestFields.CodMensajeTipo]: schemaSelectionOption.optional()

  // [EtapaRequestFields.Nombre]: z.string().optional()
})

type SchemaFiltroEtapaType = z.infer<typeof schemaFiltroEtapas>

export const TablaEtapasABM = () => {
  const [dialogNewOpen, setDialogNewOpen] = useState<boolean>(false)
  const [dialogEditOpen, setDialogEditOpen] = useState<Etapa>()
  const { getPaisLogoPorCodigo, paisLogos, loading } = usePaisLogo()

  const { askConfirmation } = useConfirmationDialog()

  const columnas: GridColsDef<Etapa>[] = [
    { headerName: 'Etapa', field: EtapaViewFields.NombreEtapa, flex: 4 },
    {
      headerName: 'Pais',
      field: EtapaViewFields.NombreSistemaPrincipal,
      flex: 2,
      renderCell: ({ row: etapa }) => (
        <Chip
          label={etapa[EtapaViewFields.NombreSistemaPrincipal]}
          size='small'
          variant='outlined'
          avatar={
            <CountryAvatar countryCode={etapa[EtapaViewFields.CodSistemaPrincipal]} />
          }
        />
      )
    },
    { headerName: 'Sistema', field: EtapaViewFields.NombreSistema, flex: 3 },
    { headerName: 'Duracion (dias)', field: EtapaViewFields.DiasDuracion, type: 'number' },
    { headerName: 'Costo base', field: EtapaViewFields.CostoBase, type: 'number' },
    { headerName: 'Costo diario', field: EtapaViewFields.CostoDiario, type: 'number' },
    { headerName: 'Tipo etapa', field: EtapaViewFields.TipoEtapa },
    { headerName: 'Id etapa virtual', field: EtapaViewFields.IdEtapaVirtual, type: 'number' },
    { headerName: 'Orden', field: EtapaViewFields.Orden, type: 'number' },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({ row }) => getActions(row, true)
    }
  ]

  const getActions = (etapa: Etapa, small: boolean) => [
    <GridActionsCellItem
      onClick={() => {
        const promise = HttpEtapasABM.getEtapa(etapa.id)

        toast
          .promise(promise, {
            success: 'Cargado.',
            loading: 'Cargando.',
            error: 'Error.'
          })
          .then(res => {
            setDialogEditOpen(res)
          })
      }}
      icon={
        <Tooltip title='Editar'>
          <EditIcon color='info' />
        </Tooltip>
      }
      label='Editar'
      showInMenu={small}
    />,
    <GridActionsCellItem
      onClick={() =>
        askConfirmation(() => {
          const promise = HttpEtapasABM.deleteEtapa(etapa.id)

          toast
            .promise(promise, {
              success: 'Eliminado',
              error: 'No se pudo eliminar',
              loading: 'Eliminando'
            })
            .then(() => refetch())
        })
      }
      icon={
        <Tooltip title='Eliminar'>
          <DeleteIcon color='error' />
        </Tooltip>
      }
      label='Eliminar'
      showInMenu={small}
    />
  ]

  const filtros: ItecColFilters<SchemaFiltroEtapaType> = {
    [EtapaViewFields.NombreSistemaPrincipal]: {
      type: 'select',
      name: EtapaRequestFields.CodSistemaPrincipal,
      props: {
        getOptions: () =>
          Promise.resolve(paisLogos).then(res =>
            res.map(s => ({
              value: s.id,
              label: s[EntityWithIdAndDescriptionFields.Description],
              icon: <CountryAvatar countryCode={s[PaisLogoFields.CodPais]} />
            }))
          ),
        size: 'small'
      }
    },
    [EtapaViewFields.NombreSistema]: {
      type: 'select',
      name: EtapaRequestFields.CodMensajeTipo,
      props: {
        getOptions: () =>
          HttpCacheWorkflow.getSistemasTipos().then(res =>
            res.map(s => ({
              value: s.id,
              label: s.descripcion
            }))
          ),
        size: 'small'
      }
    }
  }

  const {
    datagridProps,
    control: {
      query: { refetch }
    }
  } = useItecDataGrid({
    pagination: true,
    columns: columnas,
    getEntitiesPaginated: (pagination, filter) =>
      HttpEtapasABM.getEtapas(
        pagination,
        filter
          ? {
            codSistemaPrincipal: filter[EtapaRequestFields.CodSistemaPrincipal]?.value,
            [EtapaRequestFields.CodMensajeTipo]: filter[EtapaRequestFields.CodMensajeTipo]?.value
          }
          : {}
      ),
    density: 'compact',
    filters: filtros
  })

  // const { control, handleSubmit } = useForm<SchemaFiltroEtapaType>({
  //     resolver: zodResolver(schemaFiltroEtapas)
  // })

  // TODO: select clearables
  return (
    <WrapperTable>
      <Card>
        <CardHeader
          title='Administración de etapas'
          action={
            <IconButton disabled={datagridProps.loading} onClick={() => refetch()}>
              <CachedIcon />
            </IconButton>
          }
        />
        <ItecDataGrid {...datagridProps} />
      </Card>
      <>
        <Dialog open={dialogNewOpen} fullWidth maxWidth='lg'>
          {dialogNewOpen && (
            <DialogEtapaABM
              closeDialog={() => {
                setDialogNewOpen(false)
                refetch()
              }}
            />
          )}
        </Dialog>
        <Dialog open={!!dialogEditOpen} fullWidth maxWidth='lg'>
          {!!dialogEditOpen && (
            <DialogEtapaABM
              closeDialog={() => {
                setDialogEditOpen(undefined)
                refetch()
              }}
              etapa={dialogEditOpen}
            />
          )}
        </Dialog>
      </>
    </WrapperTable>
  )
}
